import { findEnvVar } from 'lib/findEnvVar';
import { camelCase, isArray, isObject, transform } from 'lodash';
// Mocks
import clearinghouse from './fixtures/clearinghouse.json';
import consider from './fixtures/domestic-consider.json';
import drugScheduled from './fixtures/domestic-drug-schedule.json';
import drugClear from './fixtures/domestic-drug-clear.json';
import drugConsider from './fixtures/domestic-drug-consider.json';
import drugAlcoholClearinghouse from './fixtures/drug_alcohol_clearinghouse-clear.json';
import occHealthClear from './fixtures/domestic-occ-health-clear.json';
import occHealthConsider from './fixtures/domestic-occ-health-consider.json';
import drugClearOccHealthCanceled from './fixtures/domestic-drug-clear-occ-health-canceled.json';
import occHealthClearWithCanceledExam from './fixtures/domestic-occ-health-clear-with-canceled-exam.json';
import international_consider from './fixtures/international-consider.json';
import international_clear from './fixtures/international-clear.json';
import pendingOutage from './fixtures/domestic-pending-outage.json';
import ssnTraceReportable from './fixtures/domestic-ssn-trace-consider.json';
import pendingDelay from './fixtures/domestic-pending-delay.json';

// Convert out JSON objects keys that use snake_case to camelCase
// https://stackoverflow.com/questions/59769649/recursively-convert-an-object-fields-from-snake-case-to-camelcase#comment123909399_59771233
const camelize = (obj: Record<string, unknown>) =>
  transform(
    obj,
    (result: Record<string, unknown>, value: unknown, key: string, target) => {
      const camelKey = isArray(target) ? key : camelCase(key);
      result[camelKey] = isObject(value)
        ? camelize(value as Record<string, unknown>)
        : value;
    },
  );

const MOCK_REPORTS = {
  clearinghouse: { report: camelize(clearinghouse) },
  consider: { report: camelize(consider) },
  drugScheduled: { report: camelize(drugScheduled) },
  drugClear: { report: camelize(drugClear) },
  drugConsider: { report: camelize(drugConsider) },
  drugClearOccHealthCanceled: {
    report: camelize(drugClearOccHealthCanceled),
  },
  drugAlcoholClearinghouse: {
    report: camelize(drugAlcoholClearinghouse),
  },
  occHealthClear: { report: camelize(occHealthClear) },
  occHealthConsider: { report: camelize(occHealthConsider) },
  occHealthClearWithCanceledExam: {
    report: camelize(occHealthClearWithCanceledExam),
  },
  international_consider: {
    report: camelize(international_consider),
  },
  international_clear: {
    report: camelize(international_clear),
  },
  ssnTraceReportable: {
    report: camelize(ssnTraceReportable),
  },
  pendingOutage: {
    report: camelize(pendingOutage),
  },
  pendingDefault: {
    report: camelize(pendingDelay),
  },
  pendingPastEta: {
    report: {
      ...camelize(pendingDelay),
      latestPredictionType: 'past_eta',
    },
  },
  pendingNewProviderEta: {
    report: {
      ...camelize(pendingDelay),
      latestPredictionType: 'new_provider_eta',
    },
  },
  pendingCandidateResolved: {
    report: {
      ...camelize(pendingDelay),
      latestPredictionType: 'candidate_exception_resolved',
    },
  },
  pendingNewScreening: {
    report: {
      ...camelize(pendingDelay),
      latestPredictionType: 'new_screening',
    },
  },
};

export const API_STATUS_PROXY_URL = findEnvVar('API_STATUS_PROXY_URL');
const AMPLITUDE_API_KEY = findEnvVar('AMPLITUDE_API_KEY');
const APP_VERSION = findEnvVar('DD_VERSION');
const AMPLITUDE_ENABLED_RAW = findEnvVar('AMPLITUDE_ENABLED');
const AMPLITUDE_ENABLED = AMPLITUDE_ENABLED_RAW
  ? JSON.parse(AMPLITUDE_ENABLED_RAW)
  : false;
const CHECKR_API_BASE = findEnvVar('CHECKR_API_BASE');
const CHECKR_APPLICANT_FAQ_PAGE =
  'https://help.checkr.com/hc/en-us/categories/360002478633-Candidates';
const CHECKR_APPLICANTS_BASE_URL = findEnvVar('CHECKR_APPLICANTS_BASE_URL');
const CHECKR_APPLICANTS_US_BASE_URL = findEnvVar(
  'CHECKR_APPLICANTS_US_BASE_URL',
);
const CHECKR_APPLICANTS_EU_BASE_URL = findEnvVar(
  'CHECKR_APPLICANTS_EU_BASE_URL',
);
const CHECKR_REGION = findEnvVar('CHECKR_REGION');
const CHECKR_APPLICANT_PORTAL = findEnvVar('CHECKR_APPLICANTS_BASE_URL');
const CHECKR_APPLY_BASE_URL = findEnvVar('CHECKR_APPLY_BASE');

const CHECKR_VERIFICATIONS_API_BASE = findEnvVar(
  'CHECKR_VERIFICATIONS_API_BASE',
);
const CHECKR_BFF_API_BASE = findEnvVar('CHECKR_BFF_API_BASE');
const PERSONAL_CHECKS_API = findEnvVar('PERSONAL_CHECKS_API');
const PERSONAL_URL = findEnvVar('PERSONAL_URL');
const CANDIDATE_STORY_FORM_NAME = 'candidate_stories';
const DISPUTE_FORM_NAME = 'disputes';
const ENV = findEnvVar('ENV');
const IS_BOT =
  (window as any)._DATADOG_SYNTHETICS_BROWSER === 'true' ||
  window.env?.req?.['x-datadog-origin'] === 'synthetics' ||
  (typeof window.env?.req?.['user-agent'] !== 'undefined' &&
    window.env?.req?.['user-agent'].match(/DatadogSynthetics$/) !== null);
const IS_PRODUCTION = ENV === 'production';
// Typed `string` (not `string | undefined`) only because these two feed
// third-party components whose props require one (Filestack `apikey`,
// ReCAPTCHA `sitekey`). If unset, those components fail at render exactly as
// they did before; this is a typing change, not a runtime one.
const FILE_STACK_API_KEY = findEnvVar('FILE_STACK_API_KEY') as string;
const RECAPTCHA_SITE_KEY = (
  IS_BOT
    ? findEnvVar('RECAPTCHA_SITE_TEST_KEY')
    : findEnvVar('RECAPTCHA_SITE_KEY')
) as string;
const SENTRY_DSN = findEnvVar('SENTRY_DSN');
const SENTRY_RELEASE = findEnvVar('SENTRY_RELEASE');
const VERIFICATION_FORM_NAME = 'verification';
const INSTANT_EXCEPTIONS_FORM_NAME = 'instantExceptions';

const SSN_CHECK_FAILURE = 'ssn_check_failure';

const CANDIDATE_STORY_SECTIONS = [
  'County Searches',
  'State Search',
  'Federal District Searches',
  'Federal Search',
];

type AutoDisputeScreenings =
  | 'County Searches'
  | 'Municipal Searches'
  | 'State Search'
  | 'Incidents';

const AUTO_DISPUTE_SCREENINGS: AutoDisputeScreenings[] = [
  'County Searches',
  'Municipal Searches',
  'State Search',
  'Incidents',
];

const CANS_STEP_SELECT = 'select';
const CANS_STEP_PROVIDE = 'provide';
const CANS_STEP_REVIEW = 'review';
const MAX_DOCUMENTS = findEnvVar('MAX_DOCUMENTS');

const SESSION_STORAGE_CANDIDATE_IDS_KEY = 'candidate_ids';
const SESSION_STORAGE_CONTACT_EMAIL_KEY = 'contact_email';
const SESSION_STORAGE_CONTACT_NAME_KEY = 'contact_name';

const SESSION_STORAGE_JWT_KEY = 'candidate_jwt';
const SESSION_STORAGE_AUTH_METHOD_KEY = 'auth_method';
const SESSION_STORAGE_TOKEN_KEY = 'token';
const SESSION_STORAGE_WORKOS_DOB_PENDING_KEY = 'workos_dob_pending';
const SESSION_STORAGE_OAUTH_PREV_LOCATION_KEY = 'oauth_prev_location';

const AUTH_METHODS = {
  CHECKR_PROFILE: 'checkr_profile_auth',
  SSN: 'ssn',
  INTERNATIONAL: 'international',
  WORKOS_EMAIL: 'workos_email',
  WORKOS_GOOGLE: 'workos_google',
  UNIFIED_LOGIN: 'unified_login',
} as const;

const DISPUTE_SECTIONS = {
  ssnTrace: { id: 'dispute_ssn_trace', title: 'SSN Verification' },
  evictionSearch: { id: 'dispute_eviction_search', title: 'Civil Search' },
  sexOffenderSearch: {
    id: 'dispute_sex_offender_search',
    title: 'Sex Offender Search',
  },
  terroristWatchlistSearch: {
    id: 'dispute_terrorist_watchlist_search',
    title: 'Global Watchlist Search',
  },
  healthcareSanctionsSearches: {
    id: 'healthcare_sanctions_searches',
    title: 'Healthcare Sanctions Searches',
  },
  internationalCriminalSearches: {
    id: 'dispute_international_criminal_search',
    title: 'International Searches',
  },
  internationalCriminalSearchesV2: {
    id: 'dispute_international_criminal_search',
    title: 'International Criminal Searches',
  },
  internationalCanadaBcEnhancedCriminalSearch: {
    id: 'dispute_international_canada_bc_enhanced_criminal_search',
    title: 'International Canada BC Enhanced Criminal Search',
  },
  internationalGlobalWatchlistSearch: {
    id: 'dispute_international_global_watchlist_search',
    title: 'International Global Watchlist Search',
  },
  internationalEducationVerification: {
    id: 'dispute_international_education_verification',
    title: 'International Education Verification',
  },
  internationalEmploymentVerification: {
    id: 'dispute_international_employment_verification',
    title: 'International Employment Verification',
  },
  internationalAdverseMediaSearches: {
    id: 'dispute_international_adverse_media_search',
    title: 'International Adverse Media Searches',
  },
  internationalIdentityDocumentValidation: {
    id: 'dispute_international_identity_document_validation',
    title: 'International Identity Document Validation',
  },
  internationalMotorVehicleReports: {
    id: 'dispute_international_motor_vehicle_report',
    title: 'International Motor Vehicle Report',
  },
  nationalCriminalSearch: {
    id: 'dispute_national_criminal_search',
    title: 'National Search',
  },
  stateCriminalSearches: {
    id: 'dispute_state_criminal_search',
    title: 'State Search',
  },
  pointerStateCriminalSearches: {
    id: 'pointer_dispute_state_criminal_search',
    title: 'Pointer State Search',
  },
  federalDistrictCriminalSearches: {
    id: 'dispute_federal_district_criminal_search',
    title: 'Federal District Searches',
  },
  federalDistrictCivilSearches: {
    id: 'dispute_federal_district_civil_search',
    title: 'Federal District Civil Searches',
  },
  countyCriminalSearches: {
    id: 'dispute_county_criminal_search',
    title: 'County Searches',
  },
  municipalCriminalSearches: {
    id: 'dispute_municipal_criminal_search',
    title: 'Municipal Searches',
  },
  countyCivilSearches: {
    id: 'dispute_county_civil_search',
    title: 'County Civil Searches',
  },
  childAbuseSearches: {
    id: 'dispute_child_abuse_search',
    title: 'Child Abuse Searches',
  },
  arrestSearch: { id: 'dispute_arrest_search', title: 'Arrest Search' },
  motorVehicleReport: {
    id: 'dispute_motor_vehicle_report',
    title: 'Motor Vehicle Report',
  },
  employmentVerification: {
    id: 'dispute_employment_verification',
    title: 'Employment Verification',
  },
  educationVerification: {
    id: 'dispute_education_verification',
    title: 'Education Verification',
  },
  personalReferenceVerifications: {
    id: 'dispute_personal_reference_verification',
    title: 'Personal Reference Verifications',
  },
  professionalReferenceVerifications: {
    id: 'dispute_professional_reference_verification',
    title: 'Professional Reference Verifications',
  },
  abuseRegistrySearch: {
    id: 'dispute_abuse_registry_search',
    title: 'Abuse Registry Search',
  },
  socialMediaScreenings: {
    id: 'dispute_social_media_search',
    title: 'Social Media Search',
  },
  facisSearch: { id: 'dispute_facis_search', title: 'FACIS' },
  creditReport: { id: 'dispute_credit_report', title: 'Credit Report' },
  drugScreening: { id: 'dispute_drug_screening', title: 'Drug Screening' },
  professionalLicenseVerifications: {
    id: 'professional_license_verification',
    title: 'Professional License Verification',
  },
  clearinghouseSearch: {
    id: 'clearinghouse_report',
    title: 'Clearinghouse Report',
  },
  safetyAllianceSearch: {
    id: 'dispute_safety_alliance_search',
    title: 'Incidents',
  },
};

const HELP_CENTER_LINKS = {
  candidatesHelpCenter:
    'https://help.checkr.com/hc/en-us/categories/360002478633-Candidates?utm_source=candidate_portal&utm_medium=referral&utm_content=Help_Center_Header',
  contactUs:
    'https://help.checkr.com/hc/en-us/requests/new?ticket_form_id=360004159513',
};

const MS_IN_A_DAY = 86400000;
const SECONDS_IN_A_DAY = 86400;

// These constants are also enforced by the monolith
const CANDIDATES_DOCUMENTS_SIZE_MAX_BYTES = 30000000;
const CANDIDATES_DOCUMENTS_SIZE_MIN_BYTES = 1000;

const FLAGR_ENTITY_ID_KEY = 'flagr_entity_id';

const FLAGR_KEYS = {};

const UNMASK_DOB_CONTEXTS = Object.freeze({
  TEST: {
    geoIds: ['984f0795b2ca2dedb7fa3782'],
    country: 'CA',
  },
  LYFT: {
    geoIds: [
      'a8a73903c6f9d8179aed2349',
      'b118c3231348727582f2c848', // YXE Geo ID https://checkr.atlassian.net/browse/ECDEX-1571
      '3998c8287336ec1107e115ef', // YQR Geo ID https://checkr.atlassian.net/browse/ECDEX-1571
      // Add additional LYFT geo_ids here as needed
    ],
    country: 'CA',
  },
});

const ADA_JS_SUPPORT_SCRIPT = 'https://static.ada.support/embed2.js';
const ADA_SCRIPT_ID = '__ada';
const ADA_CONTROL_ID = 'ada-entry';
const DECAGON_JS_SUPPORT_SCRIPT =
  'https://decagon.ai/loaders/nWNVuzS_checkr.js';
const DECAGON_SCRIPT_ID = '__decagon';
const FLAGR_URL = findEnvVar('FLAGR_URL');

const HARNESS_SDK_KEY = findEnvVar('HARNESS_SDK_KEY');
const UNIFIED_LOGIN_BROKER_URL = findEnvVar('UNIFIED_LOGIN_BROKER_URL');
const UNIFIED_LOGIN_TENANT_ISSUER = findEnvVar('UNIFIED_LOGIN_TENANT_ISSUER');
const UNIFIED_LOGIN_TENANT_CLIENT_ID = findEnvVar(
  'UNIFIED_LOGIN_TENANT_CLIENT_ID',
);
const ACCOUNT_URI_COOKIE_NAME = 'checkr_account_uri';
const MAX_ALLOWED_UPLOAD_DOCS_QTY = 10;

const CHARGE_ATTRS = Object.freeze({
  // attribute from api,  human readable for display
  arrestDate: 'Arrest Date',
  charge: 'Charge',
  chargeDate: 'Charge Date',
  chargeType: 'Charge Type',
  convictionDate: 'Conviction Date',
  court: 'Court',
  defendant: 'Defendant',
  deposition: 'Deposition',
  depositionDate: 'Deposition Date',
  disposition: 'Disposition',
  dispositionDate: 'Disposition Date',
  jailTime: 'Sentenced To Jail Time', // changed due to litigation risk
  nextCourtDate: 'Next Court Date',
  offenseDate: 'Offense Date',
  plaintiff: 'Plaintiff',
  plea: 'Plea',
  prisonTime: 'Sentenced To Prison Time', // changed due to litigation risk
  probationStatus: 'Probation Status',
  probationTime: 'Sentenced To Probation Time', // changed due to litigation risk
  releaseDate: 'Release Date',
  restitution: 'Sentenced To Restitution', // changed due to litigation risk
  sentence: 'Other Sentencing Info', // changed due to ECRQ-90
  sentenceDate: 'Sentence Date',
});

const CANDIDATE_STORY_MAX_CHARACTER_LIMIT = 65535;

const MVR_MATRIX_TYPES = ['mvr_consent'];

const MVR_DOC_TYPES_WITH_ONE_FILE_LIMIT = [
  'driver_license',
  'driver_license_history',
];

const PATHS = {
  REPORTS_OVERVIEW: '/reports',
  INTERNATIONAL: '/international',
  INTERNATIONAL_REPORTS_OVERVIEW: '/international/reports',
  WORKOS_LOGIN: '/login/email',
};

const MVR_INCIDENT_FIELDS = [
  'description',
  'accidentDate',
  'issuedDate',
  'startDate',
  'endDate',
  'date',
  'city',
  'disposition',
  'dispositionDate',
  'points',
  'state',
  'stateCode',
  'additionalInfo',
];

const MVR_VIOLATION_FIELDS = [
  'description',
  'issuedDate',
  'dispositionDate',
  'disposition',
  'points',
  'city',
  'state',
  'acdCode',
  'additionalInfo',
];

const MVR_ACCIDENT_FIELDS = [
  'description',
  'accidentDate',
  'severity',
  'city',
  'points',
  'state',
];

const MVR_SUSPENSION_FIELDS = ['description', 'startDate', 'endDate', 'state'];

const MVR_MISCELLANEOUS_FIELDS = ['description', 'date', 'additionalInfo'];

const MVR_MEDICAL_CERTIFICATE_FIELDS = [
  'status',
  'selfCertification',
  'issuedDate',
  'expirationDate',
  'examinerFullName',
  'examinerSpecialty',
  'examinerLicenseNumber',
  'examinerLicenseState',
  'examinerRegistrationNumber',
  'examinerPhoneNumber',
];

const INTERNATIONAL_TEST_ACCOUNT = 'international_test';

const US_TERRITORIES = [
  'AS', // American Samoa
  'GU', // Guam
  'MP', // Northern Mariana Islands
  'PR', // Puerto Rico
  'US', // United States
  'VI', // US Virgin Islands
];

const MAX_FILE_SIZE = 31457280; // 30MB

const ALLOWED_MIME_TYPES = [
  'image/gif',
  'image/jpeg',
  'image/jpg',
  'image/heic',
  'image/png',
  'image/tiff',
  'image/bmp',
  '.pdf',
];

const PDF_MIME_TYPES = ['.pdf', 'application/pdf'];

export {
  CANDIDATE_STORY_FORM_NAME,
  CANDIDATE_STORY_MAX_CHARACTER_LIMIT,
  CANDIDATE_STORY_SECTIONS,
  CANDIDATES_DOCUMENTS_SIZE_MAX_BYTES,
  CANDIDATES_DOCUMENTS_SIZE_MIN_BYTES,
  CANS_STEP_PROVIDE,
  CANS_STEP_REVIEW,
  CANS_STEP_SELECT,
  CHECKR_API_BASE,
  CHECKR_APPLICANT_FAQ_PAGE,
  CHECKR_APPLICANT_PORTAL,
  CHECKR_APPLICANTS_BASE_URL,
  CHECKR_APPLICANTS_EU_BASE_URL,
  CHECKR_APPLICANTS_US_BASE_URL,
  CHECKR_REGION,
  CHECKR_APPLY_BASE_URL,
  CHECKR_VERIFICATIONS_API_BASE,
  CHECKR_BFF_API_BASE,
  PERSONAL_CHECKS_API,
  PERSONAL_URL,
  UNMASK_DOB_CONTEXTS,
  DISPUTE_FORM_NAME,
  DISPUTE_SECTIONS,
  ENV,
  FILE_STACK_API_KEY,
  HELP_CENTER_LINKS,
  IS_PRODUCTION,
  IS_BOT,
  INTERNATIONAL_TEST_ACCOUNT,
  MAX_DOCUMENTS,
  MAX_FILE_SIZE,
  MOCK_REPORTS,
  MS_IN_A_DAY,
  RECAPTCHA_SITE_KEY,
  SECONDS_IN_A_DAY,
  SENTRY_DSN,
  SENTRY_RELEASE,
  APP_VERSION,
  SESSION_STORAGE_CANDIDATE_IDS_KEY,
  SESSION_STORAGE_CONTACT_EMAIL_KEY,
  SESSION_STORAGE_CONTACT_NAME_KEY,
  SESSION_STORAGE_JWT_KEY,
  SESSION_STORAGE_AUTH_METHOD_KEY,
  SESSION_STORAGE_TOKEN_KEY,
  SESSION_STORAGE_WORKOS_DOB_PENDING_KEY,
  SESSION_STORAGE_OAUTH_PREV_LOCATION_KEY,
  AUTH_METHODS,
  SSN_CHECK_FAILURE,
  VERIFICATION_FORM_NAME,
  INSTANT_EXCEPTIONS_FORM_NAME,
  MAX_ALLOWED_UPLOAD_DOCS_QTY,
  FLAGR_URL,
  HARNESS_SDK_KEY,
  UNIFIED_LOGIN_BROKER_URL,
  UNIFIED_LOGIN_TENANT_ISSUER,
  UNIFIED_LOGIN_TENANT_CLIENT_ID,
  ACCOUNT_URI_COOKIE_NAME,
  CHARGE_ATTRS,
  FLAGR_ENTITY_ID_KEY,
  FLAGR_KEYS,
  AMPLITUDE_API_KEY,
  AMPLITUDE_ENABLED,
  MVR_MATRIX_TYPES,
  MVR_DOC_TYPES_WITH_ONE_FILE_LIMIT,
  PATHS,
  ADA_JS_SUPPORT_SCRIPT,
  ADA_SCRIPT_ID,
  ADA_CONTROL_ID,
  DECAGON_JS_SUPPORT_SCRIPT,
  DECAGON_SCRIPT_ID,
  MVR_INCIDENT_FIELDS,
  MVR_VIOLATION_FIELDS,
  MVR_ACCIDENT_FIELDS,
  MVR_SUSPENSION_FIELDS,
  MVR_MISCELLANEOUS_FIELDS,
  MVR_MEDICAL_CERTIFICATE_FIELDS,
  US_TERRITORIES,
  AUTO_DISPUTE_SCREENINGS,
  ALLOWED_MIME_TYPES,
  PDF_MIME_TYPES,
};
